import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

interface MasterDetailLayoutProps {
    /** Left (master) panel content — typically a filter toolbar + scrollable list. */
    master: ReactNode
    /** Right (detail) panel content — context switches based on the master selection. */
    detail: ReactNode
    /** Optional footer region for the master panel (e.g. bulk-action bar + pagination). Rendered with a top border below `master`. */
    masterFooter?: ReactNode
    /** Width of the master panel as a CSS width value. Defaults to `56%`. */
    masterWidth?: string
    /** Minimum height of the whole container. Defaults to `500px`. */
    minHeight?: string
    masterTestId?: string
    detailTestId?: string
    'data-testid'?: string
}

/**
 * Bordered master-detail container: a left panel (table, list) next to a right panel
 * (detail view). The master panel is a flex column so children can use `flex-1`
 * (e.g. `ScrollableBox grow`) to fill the available height between toolbar and
 * optional footer.
 */
export function MasterDetailLayout({
    master,
    detail,
    masterFooter,
    masterWidth = '56%',
    minHeight = '500px',
    masterTestId,
    detailTestId,
    'data-testid': testId,
}: MasterDetailLayoutProps) {
    return (
        <div
            className={cn('flex border rounded-lg bg-card')}
            style={{minHeight}}
            data-testid={testId}
        >
            <div
                className="border-r flex flex-col"
                style={{width: masterWidth}}
                data-testid={masterTestId}
            >
                {master}
                {masterFooter && (
                    <div className="border-t">
                        {masterFooter}
                    </div>
                )}
            </div>
            <div className="flex-1 overflow-y-auto p-6" data-testid={detailTestId}>
                {detail}
            </div>
        </div>
    )
}
